import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/supabase'

// import.meta.env is injected by Vite in the app build (real values here).
// SITES-SEO-1: this module is now also reachable from a non-Vite context — a
// Vercel Node function that server renders the site components transitively
// imports it. There import.meta.env is undefined, so optional chaining plus a
// process.env fallback keeps the module IMPORT-safe (never throws at load).
// The exported singleton is only ever CALLED in the browser app; the SSR
// function does its own REST fetch and never touches this client.
const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
const procEnv = (
  globalThis as unknown as { process?: { env?: Record<string, string | undefined> } }
).process?.env
export const supabaseUrl =
  viteEnv?.VITE_SUPABASE_URL ??
  procEnv?.VITE_SUPABASE_URL ??
  procEnv?.SUPABASE_URL ??
  'http://localhost:54321'
export const supabaseAnonKey =
  viteEnv?.VITE_SUPABASE_ANON_KEY ??
  procEnv?.VITE_SUPABASE_ANON_KEY ??
  procEnv?.SUPABASE_ANON_KEY ??
  'public-anon-key-placeholder'

export type { Database }

/**
 * THE-LINK-CANNOT-SIGN-YOU-IN-1 — THE CLIENT READS NOTHING OUT OF AN ADDRESS.
 *
 * supabase-js defaults to the implicit flow with `detectSessionInUrl` on, which
 * means any page loaded with `#access_token=...` in its address signs the
 * visitor in as whoever those tokens belong to. A link in a message is enough,
 * and the person who follows it ends up inside somebody else's account without
 * ever typing anything.
 *
 * So: the code flow, and nothing detected in a URL anywhere. Every way in is
 * now explicit and belongs to a page that was expecting it. A provider sign in
 * exchanges its own one time code; an emailed link carries a token hash that
 * `/auth/confirm` verifies, which is what the Supabase documentation gives for
 * a link that has to work on a different device from the one that asked.
 */
export function makeAppClient(url: string, key: string, fetchImpl?: typeof fetch) {
  return createClient<Database>(url, key, {
    auth: { flowType: 'pkce', detectSessionInUrl: false },
    ...(fetchImpl ? { global: { fetch: fetchImpl } } : {}),
  })
}

export const supabase = makeAppClient(supabaseUrl, supabaseAnonKey)
